import { NO_CATEGORY } from '@shared/constants/categories';
import { useCurrencies } from '@shared/contexts/currencies-context';
import { useOffers } from '@shared/contexts/offers';
import { useUserInfo } from '@shared/contexts/user-info';
import {
  BaseOffer, CurrencyType, GuildOffer, Offer,
} from '@shared/types/offers';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation as useTranslationHook } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Loader } from '../../components/loader';
import { OfferCard } from '../../components/offer-card';
import { RouterPathes } from '../../constants/router-paths';
import { url } from '../../constants/urls';
import { useTranslation } from '../../i18n/useTranslation';
import { nextOccurrenceFromHHmm } from '../../utils/nextOccurrenceFromHHmm';
import { CategoriesSideNav } from './components/CategoriesSideNav';

const OffersEmptyState = ({ loading }: { loading: boolean }) => {
  const { offersTranslations } = useTranslation();

  return loading ? (
    <div className="mt-[400px] flex items-center justify-center">
      <Loader
        width={80}
        height={80}
      />
    </div>
  ) : (
    <div className="flex flex-1 flex-col items-center text-center">
      <img
        src="/assets/no-offer-box.png"
        alt="no-offer-box"
        className="mb-6 size-[300px] sm:size-[400px]"
      />
      <h2 className="text-gradient-gold text-2xl font-black sm:text-3xl">
        {offersTranslations.storeIsEmpty}
      </h2>
      <p className="text-gradient-gold text-lg font-black sm:text-xl">
        {offersTranslations.weWillFillStore}
      </p>
    </div>
  );
};

export const Home: FC = () => {
  const { i18n } = useTranslationHook();
  const { offersTranslations, offerCardTranslations } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const returnLoginUrl = window.location.href;
  const redirectLoginUrl = url.redirectLogin(returnLoginUrl);
  const { data: userInfo } = useUserInfo();
  const isLoggedIn = !!userInfo;
  const { selectedCurrency, setSelectedCurrency, selectedCountryInfo } = useCurrencies();

  const {
    selectedCategory,
    setSelectedCategory,
    selectedOfferType,
    setSelectedOfferType,
    selectedCategories,
    selectedGroupedOffers,
    availableCategories,
    availableGuildCategories,
    isOffersLoading,
    isGuildFounder,
    isCryptoOffersAvailable,
    isFiatOffersAvailable,
  } = useOffers();

  const isOffersAvailable = useMemo(() => !!selectedCategories.length, [selectedCategories]);

  const handleCurrencyChange = (currency: CurrencyType) => {
    setSelectedCurrency(currency);
    setSelectedCategory(undefined);
  };

  const onProceedToCheckout = (id: string, type: 'regular' | 'guild') => {
    const params = new URLSearchParams({
      offerId: id,
      type,
      currency: selectedCurrency,
    });

    navigate({
      pathname: RouterPathes.offerCheckout,
      search: params.toString(),
    });
  };

  const scrollToCategory = (category: string, type: 'regular' | 'guild') => {
    const element = document.getElementById(`${type}-category-${category}`);
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (!selectedCategory || !selectedCategories.includes(selectedCategory)) {
      return;
    }

    scrollToCategory(selectedCategory, selectedOfferType);
  }, [selectedCategory]);

  const getCategoryName = (category: string) => (category === NO_CATEGORY
    ? i18n.t('offers.noCategory', { defaultValue: 'No Category' })
    : i18n.t(`offers.category.${category}`, { defaultValue: category }));

  const getDescription = (
    offer: BaseOffer,
    type: 'regular' | 'guild',
  ) => {
    const supply = type === 'regular'
      ? (offer as Offer).individualSupply
      : (offer as GuildOffer).guildFounderSupply;

    const maxSupply = type === 'regular'
      ? (offer as Offer).maxIndividualSupply
      : (offer as GuildOffer).maxFounderSupply;

    if (supply !== undefined) {
      const isUnlimited = maxSupply === null || maxSupply === undefined || maxSupply < 0;

      if (isUnlimited) {
        return [
          `${supply}`,
          type === 'regular'
            ? offerCardTranslations.rewardsPerPixionID
            : offerCardTranslations.rewardsPerGuildFounder,
        ].join(' ');
      }

      if (maxSupply !== undefined) {
        if (supply === 0) {
          const maxClaimDisplay = `${maxSupply}/${maxSupply}`;

          return offer.refreshTime
            ? [maxClaimDisplay, offerCardTranslations.maxClaimRefreshesIn].join(
              ' ',
            )
            : [maxClaimDisplay].join(' ');
        }

        return [
          `${supply}/${maxSupply}`,
          type === 'regular'
            ? offerCardTranslations.rewardsPerPixionID
            : offerCardTranslations.rewardsPerGuildFounder,
        ].join(' ');
      }
    }

    return undefined;
  };

  const isCategoriesSideNavVisable = isCryptoOffersAvailable || isFiatOffersAvailable;

  return (
    <div className="flex w-full gap-3">
      {isCategoriesSideNavVisable && (
        <CategoriesSideNav
          currentOfferType={selectedOfferType}
          categories={availableCategories}
          groupCategories={availableGuildCategories}
          currentCurrency={selectedCurrency}
          changeCurrency={handleCurrencyChange}
          isGuildFounder={isGuildFounder}
          scrollToCategory={setSelectedCategory}
          updateCurrentOfferType={setSelectedOfferType}
          selectedCountryInfo={selectedCountryInfo}
        />
      )}
      <div className="z-10 flex w-full flex-col">
        {
          !isLoggedIn && (
            <div className="mt-10 flex w-full items-center justify-center">
              <Link
                to={redirectLoginUrl}
                className="yellow-button font-border poppins-2xl max-h-[66px] w-full max-w-[287px] text-[22.5px] uppercase sm:max-h-[84px] sm:max-w-[828px] sm:text-[40px]"
              >
                {offersTranslations.signIn}
              </Link>
            </div>
          )
        }
        <div className="flex flex-1 flex-col">
          {isOffersAvailable
            ? selectedCategories.map((category) => {
              const categoryOffers = selectedGroupedOffers.get(category) as BaseOffer[];

              return (
                <div
                  key={category}
                  id={`${selectedOfferType}-category-${category}`}
                  className="flex flex-col gap-6 py-6 pr-4 2xl:pl-4"
                >
                  <h2 className="font-border poppins-2xl pl-4 sm:pl-0">
                    {getCategoryName(category)}
                  </h2>
                  <div className="grid grid-cols-1 place-items-center gap-10 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {categoryOffers.map((offer) => (
                      <OfferCard
                        key={offer.id}
                        type={selectedOfferType}
                        bonus={
                          offer.bonus
                            ? `${offerCardTranslations.bonus} ${offer.bonus}%`
                            : undefined
                        }
                        offerName={offer.title[currentLang]}
                        description={getDescription(offer, selectedOfferType)}
                        price={offer.price}
                        usdcPrice={offer.usdcPrice}
                        expiredAt={
                          offer.endDate ? new Date(offer.endDate) : undefined
                        }
                        imageUrl={offer.sku.imageUrl}
                        backgroundUrl={offer.sku.backgroundUrl}
                        buttonType={offer.sku.buttonType}
                        isLimitReached={
                          offer.individualSupply === 0
                          && (offer.maxGlobalSupply === -1 || offer.globalSupply > 0)
                        }
                        refreshTime={
                          offer.refreshTime
                            ? nextOccurrenceFromHHmm(offer.refreshTime)
                            : undefined
                        }
                        items={offer.sku.items}
                        displayOfferType={offer.sku.displayOfferType}
                        isSoldOut={offer.globalSupply === 0}
                        proceedToCheckout={() => onProceedToCheckout(offer.id, selectedOfferType)}
                        currency={selectedCurrency}
                        countryInfo={selectedCountryInfo}
                      />
                    ))}
                  </div>
                </div>
              );
            })
            : <OffersEmptyState loading={isOffersLoading} />}
        </div>
      </div>
    </div>
  );
};
